import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { GlobalBus } from '@core/Bus/EventBus';
import { OPEN_BET_HISTORY } from '@core/Bus/betHistoryEvents';
import {
  patchAuthUserNickname,
  resolveSkyxPlayerMenuLabel,
  applyServerNicknameFromPayload,
  useAuth,
} from '@core/gameengine/state/auth';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { sanitizeAudioStorage } from '@core/Audio/audioStorage';
import { useT } from '@core/i18n';
import { AvatarService } from '@core/Web/services/AvatarService';
import { AuthService } from '@core/Web/services/AuthService';
import { isPartnerFromSearch } from '@config/runtime.config';
import { trackSound } from '@core/analytics';
import './GameMenu.css';

export interface MenuSwitchChrome {
  trackOff: string;
  trackOn: string;
  thumbOff: string;
  thumbOn: string;
  trackOnGlow?: string;
  labelOff?: string;
  labelOn?: string;
}

// Simple Switch component
const Switch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Full on/off palette (e.g. skyx_new menu — matches auto cash out toggle). */
  palette?: MenuSwitchChrome;
  /** Legacy: when checked only (prefer `palette`). */
  checkedPalette?: { track: string; thumb: string };
}> = ({ checked, onChange, palette, checkedPalette }) => {
  const trackOff = palette?.trackOff ?? '#6C7884';
  const trackOn = palette?.trackOn ?? checkedPalette?.track ?? '#4CAF50';
  const thumbOff = palette?.thumbOff ?? '#fff';
  const thumbOn = palette?.thumbOn ?? checkedPalette?.thumb ?? '#fff';
  return (
    <div
      style={{
        width: '40px',
        height: '20px',
        borderRadius: '10px',
        background: checked ? trackOn : trackOff,
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s ease, box-shadow 0.2s ease',
        boxShadow:
          checked && palette?.trackOnGlow ? palette.trackOnGlow : undefined,
      }}
      onClick={e => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <div
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: checked ? thumbOn : thumbOff,
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          transition: 'left 0.2s ease, background 0.2s ease',
        }}
      />
    </div>
  );
};

// --- Icons ---
const ChangeAvatarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const PlayerIdIcon = () => (
  <svg
    width="60%"
    height="60%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const SKYX_PLAYER_AVATAR_STORAGE_KEY = 'skyx_new-player-avatar';

/** Viewport-fixed menu anchor — keep in sync with menuShell `top`. */
const VIEWPORT_MENU_TOP_PX = 40;
const VIEWPORT_MENU_BOTTOM_INSET_PX = 8;

const PlayerMenuAvatarIcon: React.FC<{
  imageUrl: string | null;
  fallback: React.ReactNode;
}> = ({ imageUrl, fallback }) => {
  const [useFallback, setUseFallback] = useState(!imageUrl);

  useEffect(() => {
    setUseFallback(!imageUrl);
  }, [imageUrl]);

  if (useFallback || !imageUrl) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={imageUrl}
      alt=""
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        objectFit: 'cover',
      }}
      onError={() => setUseFallback(true)}
    />
  );
};

const SoundIcon: React.FC<{ enabled: boolean }> = ({ enabled }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
    {enabled && (
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
    )}
  </svg>
);

const MusicIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18V5l12-2v13"></path>
    <circle cx="6" cy="18" r="3"></circle>
    <circle cx="18" cy="16" r="3"></circle>
  </svg>
);

const AnimationIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15.5 2.5a2.5 2.5 0 0 0-5 0" />
    <path d="M8.5 21.5a2.5 2.5 0 0 0 5 0" />
    <path d="M2.5 15.5a2.5 2.5 0 0 0 0-5" />
    <path d="M21.5 8.5a2.5 2.5 0 0 0 0 5" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
  </svg>
);

const HistoryIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 3v5h5"></path>
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"></path>
  </svg>
);

const LimitsIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 12h16" />
    <path d="M4 18h16" />
    <path d="M4 6h16" />
  </svg>
);

const HowToPlayIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const RulesIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const ProvablyFairIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const HomeIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const LogoutIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

// ------------------------
// UPDATED MenuItem
// ------------------------
const MenuItem: React.FC<{
  icon?: React.ReactNode;
  label: string;
  labelStyle?: React.CSSProperties;
  /** Applies to icon + label (e.g. toggle row on/off text color). */
  toneStyle?: React.CSSProperties;
  rightContent?: React.ReactNode;
  onClick?: () => void;
  clickable?: boolean; // NEW PROP
}> = ({
  icon,
  label,
  labelStyle,
  toneStyle,
  rightContent,
  onClick,
  clickable = false,
}) => (
  <div
    className={clickable ? 'game-menu-item' : undefined}
    style={{
      ...styles.menuItem,
      cursor: clickable ? 'pointer' : 'default',
    }}
    onClick={clickable ? onClick : undefined}
  >
    <div style={styles.menuItemLeft}>
      {icon && <span style={{ ...styles.menuIcon, ...toneStyle }}>{icon}</span>}
      <span style={{ ...labelStyle, ...toneStyle }}>{label}</span>
    </div>

    {/* Only Switch is clickable */}
    <span
      onClick={e => {
        e.stopPropagation();
      }}
    >
      {rightContent}
    </span>
  </div>
);

export interface GameMenuChrome {
  /** Background for the top “Player ID” row only. */
  playerHeaderBackground: string;
  /** Background for the rest of the dropdown (not the scoreboard). */
  menuBodyBackground: string;
  /** Nickname text in the player header row (sky-x new). */
  playerHeaderLabelColor?: string;
  /** Player avatar diameter in the menu header row (px). */
  playerAvatarSizePx?: number;
  /** Sound / music toggle track, thumb, label, and glow (skyx_new). */
  switchChrome?: MenuSwitchChrome;
  /** @deprecated Prefer `switchChrome`. */
  switchOnTrack?: string;
  /** @deprecated Prefer `switchChrome`. */
  switchOnThumb?: string;
}

/** Optional click handlers for menu rows (e.g. skyx_new overlays). */
export interface GameMenuHandlers {
  onGameRules?: () => void;
  onHowToPlay?: () => void;
  onProvablyFairSettings?: () => void;
  onGameLimits?: () => void;
  onBetHistory?: () => void;
  onChangeAvatar?: () => void;
}

export const GameMenu: React.FC<{
  onClose: () => void;
  playerId: string | null;
  /** Game-specific menu colors (e.g. skyx_new). */
  chrome?: GameMenuChrome;
  handlers?: GameMenuHandlers;
  /**
   * Mobile/tablet: fixed to viewport so the panel stacks above #gamehud bet controls.
   * Desktop: anchored under the header button (default).
   */
  positionMode?: 'header-anchor' | 'viewport-fixed';
  /** Hide the animation on/off row (skyx_new). */
  hideAnimationToggle?: boolean;
  /** When true, load avatar from GET /api/player/v1/avatars once (stable flag). */
  useServerPlayerAvatar?: boolean;
  /** Game i18n namespace (e.g. skyx_new) for label overrides. */
  i18nGameId?: string;
}> = ({
  onClose,
  playerId: _playerId,
  chrome,
  handlers,
  positionMode = 'header-anchor',
  hideAnimationToggle = false,
  useServerPlayerAvatar = false,
  i18nGameId,
}) => {
  const t = useT(i18nGameId);
  const { user, isDemo } = useAuth();
  const authNickname = useAuth(s => s.user?.nickname?.trim() ?? '');
  const profileFetchStartedRef = useRef(false);
  const PLACEHOLDER_USERNAMES = new Set([
    'lobby-user',
    'restored-user',
    'demo-user',
  ]);

  const isDisplayableName = useCallback(
    (value: string | undefined | null): value is string => {
      const trimmed = value?.trim() ?? '';
      return (
        trimmed.length > 0 && !PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())
      );
    },
    []
  );

  const activeController = (
    window as any
  ).playgrid?.getCurrentGameController?.();
  const useGameStore = resolveGameStoreHook(activeController);
  const { allBets, betNoA, betNoB } = useGameStore((s: any) => ({
    allBets: (s.allBets || []) as Array<{ player: string; betNo: number }>,
    betNoA: s.bets?.A?.betNo as number | undefined,
    betNoB: s.bets?.B?.betNo as number | undefined,
  }));

  const myBetNos = useMemo(
    () =>
      [betNoA, betNoB].filter(
        (n): n is number => typeof n === 'number' && n > 0
      ),
    [betNoA, betNoB]
  );

  const nicknameFromActiveBets = useMemo(() => {
    const hit = allBets.find(bet => myBetNos.includes(bet.betNo));
    return hit?.player?.trim() || '';
  }, [allBets, myBetNos]);

  useEffect(() => {
    if (!chrome || !user) return;
    if (nicknameFromActiveBets) {
      patchAuthUserNickname(nicknameFromActiveBets);
    }
  }, [chrome, user, nicknameFromActiveBets]);

  useEffect(() => {
    if (!chrome || profileFetchStartedRef.current) return;

    const token = AuthService.getInstance().getAccessToken();
    if (!token) return;

    profileFetchStartedRef.current = true;
    let cancelled = false;

    void AuthService.getInstance()
      .getPlayerInfo(token)
      .then(profile => {
        if (cancelled) return;
        applyServerNicknameFromPayload(profile);
      })
      .catch(() => {
        // Profile may omit nickname until the player joins a table.
      });

    return () => {
      cancelled = true;
      profileFetchStartedRef.current = false;
    };
    // Intentionally once per menu mount — chrome is recreated each Header render.
  }, []);

  const menuPlayerLabel = useMemo(() => {
    if (chrome) {
      return resolveSkyxPlayerMenuLabel(user, nicknameFromActiveBets);
    }

    const username = user?.username?.trim() || '';
    return isDisplayableName(username) ? username : '';
  }, [chrome, isDisplayableName, nicknameFromActiveBets, user, authNickname]);

  const playerHeaderLabel = menuPlayerLabel;

  /** Sky-X menu: keep avatar row visible even when API omits username (prod). */
  const showSkyxPlayerRow =
    Boolean(chrome) && Boolean(handlers?.onChangeAvatar);
  const audioSnapshot = useMemo(() => sanitizeAudioStorage(), []);
  // Initialize state from localStorage immediately to avoid flicker
  const [soundEnabled, setSoundEnabled] = useState(
    () => !audioSnapshot.sfxMuted
  );
  const [musicEnabled, setMusicEnabled] = useState(
    () => !audioSnapshot.musicMuted
  );
  const [animationEnabled, setAnimationEnabled] = useState(true);
  const [playerAvatarUrl, setPlayerAvatarUrl] = useState<string | null>(null);

  const readStoredSkyxAvatar = useCallback(() => {
    try {
      const raw = localStorage.getItem(SKYX_PLAYER_AVATAR_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as {
        imageUrl?: string;
        avatarKey?: string;
        avatarId?: string;
      };
    } catch {
      return null;
    }
  }, []);

  const loadPlayerAvatar = useCallback(async () => {
    if (!useServerPlayerAvatar) return;

    try {
      const url =
        await AvatarService.getInstance().resolveMenuPlayerAvatarUrl(
          readStoredSkyxAvatar
        );
      setPlayerAvatarUrl(url || null);
    } catch {
      setPlayerAvatarUrl(null);
    }
  }, [readStoredSkyxAvatar, useServerPlayerAvatar]);

  useEffect(() => {
    if (!useServerPlayerAvatar) {
      setPlayerAvatarUrl(null);
      return;
    }
    void loadPlayerAvatar();
  }, [useServerPlayerAvatar, loadPlayerAvatar]);

  useEffect(() => {
    if (!useServerPlayerAvatar) return;

    const refresh = () => void loadPlayerAvatar();
    const unsubPlayer = GlobalBus.on('player:avatar-changed', refresh);
    const unsubSkyx = GlobalBus.on('skyx_new:player-avatar-changed', refresh);
    return () => {
      unsubPlayer();
      unsubSkyx();
    };
  }, [loadPlayerAvatar, useServerPlayerAvatar]);

  useEffect(() => {
    // Handler for SFX state
    const handleSfxState = (payload: { isMuted: boolean }) => {
      const newValue = !payload.isMuted;
      console.log('[sound-debug] Menu: Received audio:state:changed event', {
        isMuted: payload.isMuted,
        willSetSoundEnabled: newValue,
      });
      setSoundEnabled(newValue);
    };
    const unsubscribeSfx = GlobalBus.on('audio:state:changed', handleSfxState);

    // Handler for Music state
    const handleMusicState = (payload: { isMuted: boolean }) => {
      const newValue = !payload.isMuted;
      console.log(
        '[sound-debug] Menu: Received audio:music:state:changed event',
        {
          isMuted: payload.isMuted,
          willSetMusicEnabled: newValue,
        }
      );
      setMusicEnabled(newValue);
    };
    const unsubscribeMusic = GlobalBus.on(
      'audio:music:state:changed',
      handleMusicState
    );

    // Request initial state once on mount if audio service hasn't emitted it yet
    // The audio service emits initial state on construction, but we request it here
    // as a fallback in case the Menu mounts before the audio service is ready
    const timeoutId = setTimeout(() => {
      console.log(
        '[sound-debug] Menu: Requesting initial audio state (fallback)'
      );
      GlobalBus.emit('audio:get:state');
      GlobalBus.emit('audio:get:music:state');
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      unsubscribeSfx();
      unsubscribeMusic();
    };
  }, []);

  const handleSoundToggle = (enabled: boolean) => {
    console.log('[sound-debug] Menu: handleSoundToggle called', {
      enabled,
    });
    GlobalBus.emit('audio:set:sound', { enabled });
    setSoundEnabled(enabled);
    trackSound(!enabled, 'sfx');
  };

  const handleMusicToggle = (enabled: boolean) => {
    console.log('[sound-debug] Menu: handleMusicToggle called', {
      enabled,
    });
    GlobalBus.emit('audio:set:music', { enabled });
    setMusicEnabled(enabled);
    trackSound(!enabled, 'music');
  };

  const handleGoPortal = () => {
    const playgrid = (window as any).playgrid;
    if (playgrid?.goLobby) playgrid.goLobby();
    onClose();
  };

  // Partner embeds (?isPartner=true): hide Sign Out — exit via Home / Close only.
  const isPartner = useMemo(() => isPartnerFromSearch(), []);

  // const handleLogout = () => {
  //   setBalance(0);
  //   onClose();
  //   console.log('User logged out from menu');
  // };

  const useChrome = Boolean(chrome);
  const isViewportFixed = positionMode === 'viewport-fixed';
  const [viewportMenuMaxHeight, setViewportMenuMaxHeight] = useState(
    () =>
      `calc(100dvh - ${VIEWPORT_MENU_TOP_PX + VIEWPORT_MENU_BOTTOM_INSET_PX}px)`
  );

  useEffect(() => {
    if (!isViewportFixed) return;

    const syncMaxHeight = () => {
      const height =
        window.innerHeight -
        VIEWPORT_MENU_TOP_PX -
        VIEWPORT_MENU_BOTTOM_INSET_PX;
      setViewportMenuMaxHeight(`${Math.max(height, 120)}px`);
    };

    syncMaxHeight();
    const onOrientation = () => window.setTimeout(syncMaxHeight, 150);
    window.addEventListener('resize', syncMaxHeight);
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      window.removeEventListener('resize', syncMaxHeight);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, [isViewportFixed]);

  const menuShell: React.CSSProperties = {
    ...styles.menuContainer,
    ...(isViewportFixed
      ? {
          position: 'fixed',
          top: `${VIEWPORT_MENU_TOP_PX}px`,
          right: '8px',
          zIndex: 10050,
          maxHeight: viewportMenuMaxHeight,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      : {}),
    ...(useChrome && chrome
      ? {
          background: chrome.menuBodyBackground,
          backdropFilter: 'none',
        }
      : {}),
  };

  const caretColor =
    useChrome && chrome
      ? chrome.menuBodyBackground
      : (styles.menuContainer.background as string);

  const showPlayerHeader = Boolean(playerHeaderLabel) || showSkyxPlayerRow;

  const playerHeaderWrap: React.CSSProperties | undefined =
    useChrome && chrome && showPlayerHeader
      ? {
          padding: '10px 20px 2px',
          borderRadius: '12px 12px 0 0',
        }
      : undefined;

  const bodyWrap: React.CSSProperties = {
    ...styles.menuContent,
    ...(useChrome && chrome ? { padding: '8px' } : {}),
    ...(isViewportFixed
      ? {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
        }
      : {}),
  };

  const switchPalette =
    chrome?.switchChrome ??
    (chrome?.switchOnTrack && chrome?.switchOnThumb
      ? {
          trackOff: '#6C7884',
          trackOn: chrome.switchOnTrack,
          thumbOff: '#fff',
          thumbOn: chrome.switchOnThumb,
        }
      : undefined);

  const menuToggleTone = (
    enabled: boolean
  ): React.CSSProperties | undefined => {
    if (!switchPalette?.labelOff || !switchPalette?.labelOn) {
      return undefined;
    }
    return { color: enabled ? switchPalette.labelOn : switchPalette.labelOff };
  };

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: isViewportFixed ? `${VIEWPORT_MENU_TOP_PX - 8}px` : '2px',
          right: '7px',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderBottom: `8px solid ${caretColor}`,
          pointerEvents: 'none',
          zIndex: isViewportFixed ? 10051 : 2001,
        }}
      />
      <div style={menuShell} data-skyx-game-menu>
        {showPlayerHeader && (
          <>
            <div
              style={
                playerHeaderWrap
                  ? { ...playerHeaderWrap, flexShrink: 0 }
                  : { padding: '0 8px', paddingTop: 8, flexShrink: 0 }
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <span
                    style={{
                      ...styles.menuIcon,
                      flexShrink: 0,
                      color: chrome?.playerHeaderLabelColor ?? '#ffffff',
                      ...(typeof chrome?.playerAvatarSizePx === 'number'
                        ? {
                            width: `${chrome.playerAvatarSizePx}px`,
                            height: `${chrome.playerAvatarSizePx}px`,
                          }
                        : {}),
                    }}
                  >
                    {useServerPlayerAvatar ? (
                      <PlayerMenuAvatarIcon
                        imageUrl={playerAvatarUrl}
                        fallback={<PlayerIdIcon />}
                      />
                    ) : (
                      <PlayerIdIcon />
                    )}
                  </span>
                  {playerHeaderLabel ? (
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        color: chrome?.playerHeaderLabelColor ?? '#ffffff',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: 1.25,
                        overflow: chrome ? 'visible' : 'hidden',
                        textOverflow: chrome ? 'clip' : 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {playerHeaderLabel}
                    </span>
                  ) : null}
                </div>
                {handlers?.onChangeAvatar ? (
                  <button
                    type="button"
                    onClick={() => {
                      handlers.onChangeAvatar?.();
                      onClose();
                    }}
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '30px',
                      height: '30px',
                      padding: 0,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.85)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    aria-label={t('ui.menu.change_avatar')}
                  >
                    <ChangeAvatarIcon />
                  </button>
                ) : null}
              </div>
            </div>
          </>
        )}

        <div
          style={bodyWrap}
          {...(isViewportFixed ? { 'data-skyx-game-menu-scroll': true } : {})}
        >
          {showPlayerHeader && (
            <div style={styles.separatorRow}>
              <div style={styles.separator} />
            </div>
          )}
          {/* Toggles - NOT clickable rows */}
          <MenuItem
            icon={<SoundIcon enabled={soundEnabled} />}
            label={t('ui.menu.sound')}
            toneStyle={menuToggleTone(soundEnabled)}
            rightContent={
              <Switch
                key="menu-sound"
                checked={soundEnabled}
                onChange={handleSoundToggle}
                palette={switchPalette}
              />
            }
          />

          <MenuItem
            icon={<MusicIcon />}
            label={t('ui.menu.music')}
            toneStyle={menuToggleTone(musicEnabled)}
            rightContent={
              <Switch
                key="menu-music"
                checked={musicEnabled}
                onChange={handleMusicToggle}
                palette={switchPalette}
              />
            }
          />

          {!hideAnimationToggle && (
            <MenuItem
              icon={<AnimationIcon />}
              label={t('ui.menu.animation')}
              rightContent={
                <Switch
                  checked={animationEnabled}
                  onChange={setAnimationEnabled}
                  palette={switchPalette}
                />
              }
            />
          )}

          <div style={styles.separatorRow}>
            <div style={styles.separator} />
          </div>

          {/* Normal clickable items */}
          <MenuItem
            icon={<HistoryIcon />}
            label={t('ui.menu.my_bet_history')}
            clickable
            onClick={() => {
              handlers?.onBetHistory?.();
              GlobalBus.emit(OPEN_BET_HISTORY);
              onClose();
            }}
          />
          <MenuItem
            icon={<LimitsIcon />}
            label={t('ui.popuppanel.limits.title')}
            clickable
            onClick={() => {
              handlers?.onGameLimits?.();
              onClose();
            }}
          />
          <MenuItem
            icon={<HowToPlayIcon />}
            label={t('ui.menu.how_to_play')}
            clickable
            onClick={() => {
              handlers?.onHowToPlay?.();
              onClose();
            }}
          />
          <MenuItem
            icon={<RulesIcon />}
            label={t('ui.menu.game_rules')}
            clickable
            onClick={() => {
              handlers?.onGameRules?.();
              onClose();
            }}
          />
          <MenuItem
            icon={<ProvablyFairIcon />}
            label={t('ui.popuppanel.provably_fair.settings')}
            clickable
            onClick={() => {
              handlers?.onProvablyFairSettings?.();
              onClose();
            }}
          />

          <div style={styles.separatorRow}>
            <div style={styles.separator} />
          </div>

          <MenuItem
            icon={<HomeIcon />}
            label={t('ui.menu.home')}
            onClick={handleGoPortal}
            clickable
          />
          {!isPartner ? (
            <MenuItem
              icon={<LogoutIcon />}
              label={isDemo ? t('ui.login.log_in') : t('ui.menu.sign_out')}
              toneStyle={isDemo ? undefined : styles.dangerTone}
              onClick={
                isDemo
                  ? () => {
                      // logout();
                      onClose();
                    }
                  : handleGoPortal
              }
              clickable
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

// Styles
const styles: Record<string, React.CSSProperties> = {
  menuContainer: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: '-8px',
    width: '280px',
    maxHeight: 'calc(100dvh - 56px)',
    background: 'rgba(13, 17, 23, 0.95)',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 12px 34px rgba(0, 0, 0, 0.55)',
    zIndex: 2000,
    color: '#e8f0f8',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    pointerEvents: 'auto',
    userSelect: 'none',
  },
  menuContent: {
    padding: '8px',
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'background-color 0.2s ease',
    pointerEvents: 'auto',
  },
  menuItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  menuIcon: {
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#A0AEBB',
  },
  separatorRow: {
    padding: '4px 0',
  },
  separator: {
    height: '0.5px',
    background: 'rgba(255, 255, 255, 0.07)',
  },
  dangerTone: {
    color: '#FF6B81',
  },
};
